import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, of } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { CustomizationService } from './customization.service';
import { AddCustomizationModalComponent } from '../components/pages/customization/modal/add-customization-modal/add-customization-modal.component';
import { FNFResponse } from '../models/response';

/**
 * Opens the new-customization sheet over whatever page the user is on.
 *
 * Quick Add used to route to the Customizations list and open the sheet from
 * there, which meant watching that page appear behind the sheet and again on
 * the way out. There is nothing on the list the sheet needs except the
 * existing names, so it is fetched here and no navigation happens at all.
 */
@Injectable({ providedIn: 'root' })
export class QuickCustomizationService {
  private dialog = inject(MatDialog);
  private customizationService = inject(CustomizationService);
  private spinner = inject(NgxSpinnerService);
  private toastr = inject(ToastrService);

  /** Emits true when a customization was created. */
  openNew(): Observable<boolean> {
    const done = new Subject<boolean>();

    // The sheet checks new names against the existing ones.
    this.spinner.show();
    this.customizationService.getCustomizations().subscribe({
      next: (response: FNFResponse<any>) => {
        this.spinner.hide();
        this.openSheet(response?.data || [], done);
      },
      error: () => {
        this.spinner.hide();
        // A failed fetch only costs the duplicate-name check, so still open.
        this.openSheet([], done);
      },
    });

    return done.asObservable();
  }

  private openSheet(allCustomization: any[], done: Subject<boolean>) {
    const dialogRef = this.dialog.open(AddCustomizationModalComponent, {
      data: { customization: null, allCustomization },
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      panelClass: 'czm-fullscreen-dialog',
      // Material's scale-in would fight the sheet's slide-up.
      enterAnimationDuration: '0ms',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result === undefined || result === '') {
        done.next(false);
        done.complete();
        return;
      }
      this.save(result).subscribe((saved) => {
        done.next(saved);
        done.complete();
      });
    });
  }

  /** Shared with the Customizations page so the payload is built in one place. */
  save(customization: any): Observable<boolean> {
    const options = (customization.options || []).map((option: any) => ({
      value: option.name,
      description: option.description ?? '',
      price: option.price ? option.price : 0,
      is_default: !!option.isChecked,
      in_stock: option.inStock !== false,
    }));

    const payload = {
      name: customization.name,
      description: customization.description ?? '',
      types: options,
      remarks: customization.remarks,
      default: customization.defaultValue,
      is_multi_select: customization.inputType == 'Multi Selection',
      min_selections: customization.minSelections ?? 0,
      max_selections: customization.maxSelections ?? 0,
      is_price_different: customization.priceDiffersForEachOption,
      is_customer_visible: customization.isCustomerVisible ?? true,
    };

    const done = new Subject<boolean>();
    this.spinner.show();
    this.customizationService.addCustomization(payload).subscribe({
      next: (response: FNFResponse<any>) => {
        this.spinner.hide();
        if (!response.data) {
          const message = response.error?.error?.message || response.error?.message;
          this.toastr.error(message || 'No data found!', 'Error');
          done.next(false);
        } else {
          this.toastr.success('Customization added successfully', 'Success');
          done.next(true);
        }
        done.complete();
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        done.next(false);
        done.complete();
      },
    });
    return done.asObservable();
  }
}
